import { Type } from 'class-transformer';
import { FieldType } from 'src/app/settings/model/field-type';
import { Option, ValueField } from 'src/app/settings/model/value-field';
import { VehicleRole } from './fleetmanagement/vehicle-role';
import { StopPosition } from './departure';

export class DepartureReading {

    readonly group: string | null; // klucz grupy na potrzeby grupowania po stronie UI (ograniczenie do jednej kolumny grupującej)
    readonly id: string; // klucz odjazdu
    @Type(() => Date)
    readonly day: Date;
    readonly contract: number;

    // Dane rozkładowe
    readonly dayType: string;
    readonly brigade: string;
    readonly task: string;
    readonly line: string;
    readonly direction: string;
    readonly stopPosition: StopPosition;
    @Type(() => Date)
    readonly stopTime: Date;
    readonly stopCode: string;
    readonly stopName: string;
    readonly stopOnDemand: boolean;
    readonly stopGps: string | null = null;
    readonly distancePrev: number;
    readonly distanceOrigin: number;

    // Dane rzeczywiste
    @Type(() => Date)
    readonly timestamp: Date;
    readonly vehicleNo: string | null;
    readonly vehicleRole: VehicleRole | null; // "rodzaj" pojazdu
    readonly vehicleType: string | null; // "typ" pojazdu = rozmiar + napęd, np. "A", "Be"
    @Type(() => Date)
    readonly zoneEnter: Date | null;
    @Type(() => Date)
    readonly zoneLeave: Date | null;
    @Type(() => Date)
    readonly doorOpenOutsideZone: Date | null;
    @Type(() => Date)
    readonly doorCloseOutsideZone: Date | null;
    readonly stopPressed: boolean | null;
    readonly wheelchairPressed: boolean | null;
    @Type(() => Date)
    readonly doorEnable: Date | null;
    @Type(() => Date)
    readonly doorDisable: Date | null;
    readonly tempInside: number | null;
    readonly tempOutside: number | null;

    @Type(() => Date)
    readonly halt: Date | null;
    @Type(() => Date)
    readonly departure: Date | null;
    @Type(() => Date)
    readonly passthrough: Date | null;
    readonly delay: number | null;

    readonly passengers: FinalPassengerCounts | null;

    // Dane z review
    readonly registered: boolean;
    readonly claims: Record<number, ClaimStatus> | null;
    readonly qualifications: number[];

    get qualificationsDisplayed(): string[] | null {
        return this.qualifications.map(id => `Q${id}`);
    }

    get passengersTotal(): number {
        return this.passengers?.total;
    }

    // FIXME: Nie da się mniej "na pałę"? Przez jsonpath się nie udało...
    get passengersPerDoorCounts1Entries(): number { return this.getPassengersPerDoorCountsDisplay(1)?.enter; }
    get passengersPerDoorCounts1Exits(): number { return this.getPassengersPerDoorCountsDisplay(1)?.exit; }
    get passengersPerDoorCounts2Entries(): number { return this.getPassengersPerDoorCountsDisplay(2)?.enter; }
    get passengersPerDoorCounts2Exits(): number { return this.getPassengersPerDoorCountsDisplay(2)?.exit; }
    get passengersPerDoorCounts3Entries(): number { return this.getPassengersPerDoorCountsDisplay(3)?.enter; }
    get passengersPerDoorCounts3Exits(): number { return this.getPassengersPerDoorCountsDisplay(3)?.exit; }
    get passengersPerDoorCounts4Entries(): number { return this.getPassengersPerDoorCountsDisplay(4)?.enter; }
    get passengersPerDoorCounts4Exits(): number { return this.getPassengersPerDoorCountsDisplay(4)?.exit; }
    get passengersPerDoorCounts5Entries(): number { return this.getPassengersPerDoorCountsDisplay(5)?.enter; }
    get passengersPerDoorCounts5Exits(): number { return this.getPassengersPerDoorCountsDisplay(5)?.exit; }
    get passengersPerDoorCounts6Entries(): number { return this.getPassengersPerDoorCountsDisplay(6)?.enter; }
    get passengersPerDoorCounts6Exits(): number { return this.getPassengersPerDoorCountsDisplay(6)?.exit; }

    private getPassengersPerDoorCountsDisplay(no: number): { enter: number; exit: number } | null {
        return this.passengers?.door[no.toString()];
    }

    isFirstOrLastStopOnCourse(): boolean {
        return this.stopPosition !== StopPosition.MIDDLE;
    }

    static getValueFields(contractOptions: Option[], violationOptions: Option[]): ValueField[] {
        let i = 0;
        const contractOptionsWithInterrobangForZero = [...contractOptions, { key: '0', value: '⁉️', isHtml: false }];
        return [
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.id',
                name: DepartureReadingFields.ID, type: FieldType.TEXT, primaryKey: true, canFilter: false, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.day',
                name: DepartureReadingFields.DAY, type: FieldType.DATE, canFilter: true, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.contract',
                name: DepartureReadingFields.CONTRACT, type: FieldType.CONTRACT, canFilter: true, colNumber: ++i,
                options: contractOptionsWithInterrobangForZero
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.claims',
                name: DepartureReadingFields.CLAIMS,
                type: FieldType.VIOLATIONS_MARKER_2, canFilter: true, colNumber: ++i, canGroup: false,
                options: violationOptions
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.violation-with-status',
                name: DepartureReadingFields.VIOLATION_WITH_STATUS, type: FieldType.VIOLATION_WITH_STATUS,
                canFilter: true, visible: false, colNumber: ++i, permanentlyHidden: true
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.qualifications',
                name: DepartureReadingFields.QUALIFICATIONS,
                type: FieldType.ARRAY, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.day-type',
                name: DepartureReadingFields.COURSE_DAY_TYPE, type: FieldType.TEXT, canFilter: true, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.brigade',
                name: DepartureReadingFields.COURSE_BRIGADE, type: FieldType.TEXT, canFilter: true, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.task',
                name: DepartureReadingFields.COURSE_TASK, type: FieldType.TEXT, canFilter: true, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.line',
                name: DepartureReadingFields.COURSE_LINE, type: FieldType.TEXT, canFilter: true, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.direction',
                name: DepartureReadingFields.COURSE_DIRECTION, type: FieldType.TEXT, canFilter: true, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.stop-time',
                name: DepartureReadingFields.STOP_TIME, type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.stop-code',
                name: DepartureReadingFields.STOP_CODE, type: FieldType.TEXT, canFilter: true, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.stop-name',
                name: DepartureReadingFields.STOP_NAME, type: FieldType.TEXT, canFilter: true, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.stop-on-demand',
                name: DepartureReadingFields.STOP_ON_DEMAND, type: FieldType.BOOLEAN, canFilter: true, colNumber: ++i, canGroup: false }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.stop-distance-from-previous',
                name: DepartureReadingFields.STOP_DISTANCE_FROM_PREVIOUS,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.stop-distance-from-origin',
                name: DepartureReadingFields.STOP_DISTANCE_FROM_ORIGIN,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.stop-gps-location',
                name: DepartureReadingFields.STOP_GPS_LOCATION,
                type: FieldType.TEXT, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.vehicle-timestamp',
                name: DepartureReadingFields.RT_VEHICLE_TIMESTAMP,
                type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.vehicle-side-number',
                name: DepartureReadingFields.RT_VEHICLE_SIDE_NUMBER, type: FieldType.TEXT, canFilter: true,
                colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.vehicle-role',
                name: DepartureReadingFields.RT_VEHICLE_ROLE, type: FieldType.ENUM, canFilter: true,
                colNumber: ++i, canGroup: false,
                options: Object.keys(VehicleRole).map(key => ({ key, value: 'sakp.common.enums.vehicle-role.' + VehicleRole[key] }))
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.vehicle-type',
                name: DepartureReadingFields.RT_VEHICLE_TYPE, type: FieldType.TEXT, canFilter: true,
                colNumber: ++i, canGroup: false, translatable: true
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.zone-enter-time',
                name: DepartureReadingFields.RT_ZONE_ENTER_TIME,
                type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.zone-leave-time',
                name: DepartureReadingFields.RT_ZONE_LEAVE_TIME,
                type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.outside-zone-door-open-time',
                name: DepartureReadingFields.RT_OUTSIDE_ZONE_DOOR_OPEN_TIME,
                type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.outside-zone-door-close-time',
                name: DepartureReadingFields.RT_OUTSIDE_ZONE_DOOR_CLOSE_TIME,
                type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.temperature-inside',
                name: DepartureReadingFields.RT_TEMP_INSIDE,
                type: FieldType.FLOAT, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.temperature-outside',
                name: DepartureReadingFields.RT_TEMP_OUTSIDE,
                type: FieldType.FLOAT, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.stop-button-pressed',
                name: DepartureReadingFields.RT_STOP_BUTTON_PRESSED,
                type: FieldType.BOOLEAN, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.wheelchair-button-pressed',
                name: DepartureReadingFields.RT_WHEELCHAIR_BUTTON_PRESSED,
                type: FieldType.BOOLEAN, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.door-button-enable-time',
                name: DepartureReadingFields.RT_DOOR_BUTTON_ENABLE_TIME,
                type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.door-button-disable-time',
                name: DepartureReadingFields.RT_DOOR_BUTTON_DISABLE_TIME,
                type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.vehicle-delay',
                name: DepartureReadingFields.RT_CALCULATED_DELAY,
                type: FieldType.INTEGER, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.vehicle-departure-time',
                name: DepartureReadingFields.RT_CALCULATED_DEPARTURE_TIME,
                type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.vehicle-halt-time',
                name: DepartureReadingFields.RT_CALCULATED_HALT_TIME,
                type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.vehicle-zone-passthrough-time',
                name: DepartureReadingFields.RT_CALCULATED_PASSTHROUGH_TIME,
                type: FieldType.DATETIME, canFilter: true, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-total',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_TOTAL,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-1-enter',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_1_ENTRIES,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-1-exit',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_1_EXITS,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-2-enter',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_2_ENTRIES,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-2-exit',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_2_EXITS,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-3-enter',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_3_ENTRIES,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-3-exit',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_3_EXITS,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-4-enter',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_4_ENTRIES,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-4-exit',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_4_EXITS,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-5-enter',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_5_ENTRIES,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-5-exit',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_5_EXITS,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-6-enter',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_6_ENTRIES,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.passengers-per-door-counts-6-exit',
                name: DepartureReadingFields.RT_PASSENGER_COUNTS_PER_DOOR_6_EXITS,
                type: FieldType.INTEGER, canFilter: false, colNumber: ++i, canGroup: false
            }),

            Object.assign(new ValueField(), {
                title: 'sakp.reports.departures.columns.group',
                name: DepartureReadingFields.GROUP, type: FieldType.TEXT, primaryKey: false, canGroup: true, canFilter: false, colNumber: ++i
            })
        ];
    }

    static getTemperatureDisplay(value: number, fixed: number): string | null {
        if (value === undefined || value === null) return null;
        return Number.isFinite(value) ? `${value.toFixed(fixed)}°C` : String(value);
    }

    static getDelayDisplay(delay: number | null): string | null{
        if (!delay) return null;

        const sign = delay < 0 ? '+' : delay > 0 ? '-' : '';
        const seconds = Math.abs(delay);

        const hours = Math.floor(seconds / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
        const secs = (seconds % 60).toString().padStart(2, '0');

        return `${sign}${hours}:${minutes}:${secs}`;
    }

}


export enum DepartureReadingFields {
    GROUP = 'group',
    ID = 'id',
    DAY = 'day',
    CONTRACT = 'contract',

    COURSE_BRIGADE = 'brigade',
    COURSE_TASK = 'task',
    COURSE_DAY_TYPE = 'dayType',
    COURSE_LINE = 'line',
    COURSE_DIRECTION = 'direction',
    STOP_POSITION = 'stopPosition',
    STOP_TIME = 'stopTime',
    STOP_CODE = 'stopCode',
    STOP_NAME = 'stopName',
    STOP_ON_DEMAND = 'stopOnDemand',
    STOP_DISTANCE_FROM_PREVIOUS = 'distancePrev',
    STOP_DISTANCE_FROM_ORIGIN = 'distanceOrigin',
    STOP_GPS_LOCATION = 'stopGps',

    RT_VEHICLE_TIMESTAMP = 'timestamp',
    RT_VEHICLE_SIDE_NUMBER = 'vehicle',
    RT_VEHICLE_ROLE = 'vehicleRole',
    RT_VEHICLE_TYPE = 'vehicleType', // TODO: połączyć size z drive w type

    RT_ZONE_ENTER_TIME = 'zoneEnter',
    RT_ZONE_LEAVE_TIME = 'zoneLeave',
    RT_OUTSIDE_ZONE_DOOR_OPEN_TIME = 'doorOpenOutsideZone',
    RT_OUTSIDE_ZONE_DOOR_CLOSE_TIME = 'doorCloseOutsideZone',

    RT_TEMP_INSIDE = 'tempInside',
    RT_TEMP_OUTSIDE = 'tempOutside',

    RT_STOP_BUTTON_PRESSED = 'stopPressed',
    RT_WHEELCHAIR_BUTTON_PRESSED = 'wheelchairPressed',
    RT_DOOR_BUTTON_ENABLE_TIME = 'doorEnable',
    RT_DOOR_BUTTON_DISABLE_TIME = 'doorDisable',

    // RT_CALCULATED_DELAY = 'delay',
    RT_CALCULATED_DELAY = 'delay',
    RT_CALCULATED_HALT_TIME = 'halt',
    RT_CALCULATED_DEPARTURE_TIME = 'departure',
    RT_CALCULATED_PASSTHROUGH_TIME = 'passthrough',

    RT_PASSENGER_COUNTS_TOTAL = 'passengersTotal',
    RT_PASSENGER_COUNTS_PER_DOOR = 'passengersPerDoorCounts',
    // TODO
    RT_PASSENGER_COUNTS_PER_DOOR_1_ENTRIES = 'passengersPerDoorCounts1Entries',
    RT_PASSENGER_COUNTS_PER_DOOR_1_EXITS = 'passengersPerDoorCounts1Exits',
    RT_PASSENGER_COUNTS_PER_DOOR_2_ENTRIES = 'passengersPerDoorCounts2Entries',
    RT_PASSENGER_COUNTS_PER_DOOR_2_EXITS = 'passengersPerDoorCounts2Exits',
    RT_PASSENGER_COUNTS_PER_DOOR_3_ENTRIES = 'passengersPerDoorCounts3Entries',
    RT_PASSENGER_COUNTS_PER_DOOR_3_EXITS = 'passengersPerDoorCounts3Exits',
    RT_PASSENGER_COUNTS_PER_DOOR_4_ENTRIES = 'passengersPerDoorCounts4Entries',
    RT_PASSENGER_COUNTS_PER_DOOR_4_EXITS = 'passengersPerDoorCounts4Exits',
    RT_PASSENGER_COUNTS_PER_DOOR_5_ENTRIES = 'passengersPerDoorCounts5Entries',
    RT_PASSENGER_COUNTS_PER_DOOR_5_EXITS = 'passengersPerDoorCounts5Exits',
    RT_PASSENGER_COUNTS_PER_DOOR_6_ENTRIES = 'passengersPerDoorCounts6Entries',
    RT_PASSENGER_COUNTS_PER_DOOR_6_EXITS = 'passengersPerDoorCounts6Exits',

    CLAIMS = 'claims',
    VIOLATION_WITH_STATUS = 'violationWithStatus',
    QUALIFICATIONS = 'qualifications',
    REGISTERED = 'registered'
}

class FinalPassengerCounts {
    readonly total: number;
    readonly delta: number;
    readonly door: Record<number, { enter: number; exit: number }>;
}

export enum ClaimStatus {
    NEW = 'NEW',
    INDISPUTABLE = 'INDISPUTABLE',
    DISPUTED = 'DISPUTED',
    UPHELD = 'UPHELD',
    RELEASED = 'RELEASED'
}
